import { supabase } from '../lib/supabase';
import md5 from 'crypto-js/md5';

// =============================================================================
// AVATAR CONFIGURATION CONSTANTS - USER REQUIREMENTS
// =============================================================================

export const AVATAR_CONFIG = {
  // DiceBear API configuration
  API_BASE_URL: 'https://api.dicebear.com/7.x',
  STYLE: 'open-peeps',
  FORMAT: 'svg',
  
  // Fixed parameters per user requirements
  FIXED_PARAMS: {
    flip: false,
    scale: 75,
    size: 200,
    backgroundColor: 'transparent'
  },
  
  // Supabase storage configuration
  STORAGE_BUCKET: 'avatars',
  STORAGE_FOLDER: 'open-peeps',
  
  // Cache settings
  CACHE_DURATION_DAYS: 30,
  MAX_FILE_SIZE_KB: 100,
  
  // Gender seeds per user requirements
  SEEDS: {
    male: 'wwww',
    female: '12'
  },
  
  // Avatar options for Open Peeps - EXACT USER REQUIREMENTS
  OPTIONS: {
    // Multicultural skin colors with required color
    skinColor: ['694d3d', 'ae5d29', 'd08b5b', 'edb98a', 'ffcdbd', 'ffdbb4'],

    
    // Clothing colors - 10 hex values covering major color categories
    clothingColor: [
      'ffffff', // Dark Gray/Black
      'ffd900', // Light Blue
      'C0C0C0', // Silver/Gray
      '6e4848', // Light Gray
      'c8ad4c', // Yellow
      'FC909F', // Pink
      '31a93f', // Cream
      'a81fa8', // Navy Blue
      '0388d0', // Sky Blue
      '270707'  // White
    ],
    
    // Face expressions - EXACT options from user
    face: [
      'smile',
      'smileBig',
      'smileLOL',
      'awe',
      'blank',
      'calm',
      'cheeky',
      'contempt', // Will display as "Confident" but pass "contempt"
      'cute',
      'driven',
      'explaining',
      'eatingHappy',
      'eyesClosed',
      'old', // Will display as "Mature" but pass "old"
      'serious',
      'suspicious',
      'tired'
    ],
    
    // Facial hair - EXACT options from user
    facialHair: [
      'chin',
      'full',
      'full2',
      'full3',
      'full4',
      'goatee2',
      'moustache1',
      'moustache2',
      'moustache6'
    ],
    
    // Head/Hair options - EXACT options from user (renamed to "Hair" in UI)
    head: [
      'noHair1',
      'noHair2',
      'noHair3',
      'afro',
      'bangs',
      'bangs2',
      'bantuKnots',
      'bun',
      'bun2',
      'buns',
      'cornrows',
      'cornrows2',
      'dreads1',
      'dreads2',
      'flatTopLong',
      'grayBun',
      'grayMedium',
      'grayShort',
      'hatHip',
      'hijab',
      'long',
      'longAfro',
      'longBangs',
      'longCurly',
      'medium1',
      'medium2',
      'medium3',
      'mediumBangs',
      'mediumBangs2',
      'mediumBangs3',
      'mediumStraight',
      'short1',
      'short2',
      'short3',
      'short5',
      'twists2',
      'twists'
    ],
    
    // Accessories - EXACT options from user (all)
    accessories: [
      'eyepatch',
      'glasses',
      'glasses2',
      'glasses3',
      'glasses4',
      'glasses5',
      'sunglasses',
      'sunglasses2'
    ]
  }
};

// Default avatar configuration - SEED ONLY on initialization
export const DEFAULT_AVATAR_CONFIG = {
  // Only seed is sent initially, all other options are "none"
};

// Face display name mapping (UI renames)
export const FACE_DISPLAY_NAMES = {
  'contempt': 'Confident',
  'old': 'Mature'
};

// =============================================================================
// AVATAR SERVICE - CORE FUNCTIONALITY
// =============================================================================

export const avatarService = {
  /**
   * Generates MD5 hash of avatar configuration for cache lookup
   * @param {Object} config - Avatar configuration object
   * @returns {string} MD5 hash of configuration
   */
  generateConfigHash(config) {
    const sortedConfig = Object.keys(config || {})?.sort()?.reduce((obj, key) => {
      obj[key] = config?.[key];
      return obj;
    }, {});
    
    return md5(JSON.stringify(sortedConfig))?.toString();
  },

  /**
   * Validates avatar configuration against allowed options
   * @param {Object} config - Avatar configuration to validate
   * @returns {Object} Validated configuration with defaults for invalid values
   */
  validateConfig(config) {
    const validatedConfig = { ...DEFAULT_AVATAR_CONFIG };
    
    Object.keys(config || {})?.forEach(key => {
      if (AVATAR_CONFIG?.OPTIONS?.[key]) {
        if (AVATAR_CONFIG?.OPTIONS?.[key]?.includes(config?.[key])) {
          validatedConfig[key] = config?.[key];
        }
      }
    });
    
    return validatedConfig;
  },

  /**
   * Builds DiceBear API URL with avatar configuration
   * USER REQUIREMENTS:
   * - Only send seed on initialization
   * - Add parameters only when user selects them
   * - Never send: flip, rotate, radius, backgroundType, backgroundRotation, 
   *   translateX, translateY, clip, randomizeIds, headContrastColor
   * - FOR MALES: if facial hair selected, add facialHairProbability=100
   * - FOR FEMALES: facialHairProbability is 0 (never sent)
   * - IF ACCESSORIES CHOSEN: add accessoriesProbability=100
   * @param {Object} config - Avatar configuration
   * @param {string} seed - Seed ('John' or 'Aneka')
   * @param {string} gender - Gender for facial hair filtering
   * @returns {string} Complete DiceBear API URL
   */
  buildDiceBearURL(config, seed, gender = 'male') {
    const baseUrl = `${AVATAR_CONFIG?.API_BASE_URL}/${AVATAR_CONFIG?.STYLE}/${AVATAR_CONFIG?.FORMAT}`;
    const params = new URLSearchParams();
    
    // ALWAYS add seed (required)
    params?.append('seed', seed);
    
    // Add fixed parameters (scale, size, backgroundColor only)
    params?.append('scale', AVATAR_CONFIG?.FIXED_PARAMS?.scale);
    params?.append('size', AVATAR_CONFIG?.FIXED_PARAMS?.size);
    params?.append('backgroundColor', AVATAR_CONFIG?.FIXED_PARAMS?.backgroundColor);
    
    // Only add parameters if explicitly set (not "none" or undefined)
    if (config?.skinColor && config?.skinColor !== 'none') {
      params?.append('skinColor', config?.skinColor);
    }
    
    if (config?.clothingColor && config?.clothingColor !== 'none') {
      params?.append('clothingColor', config?.clothingColor);
    }
    
    if (config?.face && config?.face !== 'none') {
      params?.append('face', config?.face);
    }
    
    // NEVER send facial hair for female avatars
    // FOR MALES: if facial hair selected, add facialHairProbability=100
    if (gender === 'male' && config?.facialHair && config?.facialHair !== 'none') {
      params?.append('facialHair', config?.facialHair);
      params?.append('facialHairProbability', '100');
    }
    
    if (config?.head && config?.head !== 'none') {
      params?.append('head', config?.head);
    }
    
    // IF ACCESSORIES CHOSEN: add accessoriesProbability=100
    if (config?.accessories && config?.accessories !== 'none') {
      params?.append('accessories', config?.accessories);
      params?.append('accessoriesProbability', '100');
    }
    
    return `${baseUrl}?${params?.toString()}`;
  },

  /**
   * Generates static thumbnail URL for a specific option
   * This creates ONE static URL per option to avoid excessive API calls
   * @param {string} category - Category (face, head, accessories, etc.)
   * @param {string} value - Option value
   * @param {string} seed - Seed to use
   * @returns {string} Static thumbnail URL
   */
  buildStaticThumbnailURL(category, value, seed) {
    const baseUrl = `${AVATAR_CONFIG?.API_BASE_URL}/${AVATAR_CONFIG?.STYLE}/${AVATAR_CONFIG?.FORMAT}`;
    const params = new URLSearchParams();
    
    // Base parameters
    params?.append('seed', seed);
    params?.append('scale', 75);
    params?.append('size', 150); // Thumbnail size (50% larger: 100 -> 150)
    params?.append('backgroundColor', 'transparent');
    
    // Only add the specific option being previewed
    params?.append(category, value);
    
    // Add probability parameters for facial hair and accessories to ensure they show up
    if (category === 'facialHair') {
      params?.append('facialHairProbability', '100');
    }
    
    if (category === 'accessories') {
      params?.append('accessoriesProbability', '100');
    }
    
    return `${baseUrl}?${params?.toString()}`;
  },

  /**
   * Fetches SVG content from DiceBear API
   * @param {string} url - DiceBear API URL
   * @returns {Promise<string>} SVG content as string
   */
  async fetchAvatarSVG(url) {
    try {
      const response = await fetch(url);
      if (!response?.ok) {
        const errorText = await response?.text();
        throw new Error(`DiceBear API error (${response?.status}): ${errorText}`);
      }
      return await response?.text();
    } catch (error) {
      console.error('Error fetching avatar from DiceBear:', error);
      throw error;
    }
  },

  /**
   * Sanitizes SVG content before storage
   * @param {string} svgContent - Raw SVG content
   * @returns {string} Sanitized SVG content
   */
  sanitizeSVG(svgContent) {
    if (!svgContent?.includes('<svg')) {
      throw new Error('Invalid SVG content');
    }
    return svgContent;
  },

  /**
   * Generates filename for avatar storage
   * @param {string} seed - Avatar seed
   * @param {string} configHash - Configuration hash
   * @returns {string} Filename for storage
   */
  generateFilename(seed, configHash) {
    return `${AVATAR_CONFIG?.STORAGE_FOLDER}/${seed}-${configHash}.svg`;
  },

  /**
   * Checks if avatar exists in cache
   * @param {string} seed - Avatar seed
   * @param {string} configHash - Configuration hash
   * @returns {Promise<Object|null>} Cache entry or null
   */
  async checkCache(seed, configHash) {
    try {
      const { data, error } = await supabase
        ?.from('avatar_cache')
        ?.select('*')
        ?.or(`seed.eq.${seed},config_hash.eq.${configHash}`)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        console.error('Cache lookup error:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error checking avatar cache:', error);
      return null;
    }
  },

  /**
   * Updates cache access tracking
   * @param {string} cacheId - Cache entry ID
   */
  async updateCacheAccess(cacheId) {
    try {
      await supabase
        ?.from('avatar_cache')
        ?.update({
          accessed_at: new Date()?.toISOString(),
          access_count: supabase?.rpc('increment', { row_id: cacheId })
        })
        ?.eq('id', cacheId);
    } catch (error) {
      console.error('Error updating cache access:', error);
    }
  },

  /**
   * Uploads SVG to Supabase Storage
   * @param {string} svgContent - SVG content to upload
   * @param {string} filename - Target filename
   * @param {string} userId - User ID for folder organization
   * @returns {Promise<string>} Storage path for signed URL generation
   */
  async uploadSVGToStorage(svgContent, filename, userId) {
    try {
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
      
      // Organize by user ID: userId/open-peeps/seed-hash.svg
      const filePath = `${userId}/${filename}`;
      
      const { data, error } = await supabase
        ?.storage
        ?.from(AVATAR_CONFIG?.STORAGE_BUCKET)
        ?.upload(filePath, svgBlob, {
          contentType: 'image/svg+xml',
          upsert: true
        });

      if (error) {
        throw error;
      }

      // Return the storage path (not public URL since it's a private bucket)
      return filePath;
    } catch (error) {
      console.error('Error uploading SVG to storage:', error);
      throw new Error('Failed to upload avatar to storage');
    }
  },

  /**
   * Generates signed URL for private avatar access
   * @param {string} filePath - Storage file path
   * @returns {Promise<string>} Signed URL valid for 1 hour
   */
  async getSignedAvatarUrl(filePath) {
    try {
      // Validate file path exists before attempting to generate signed URL
      if (!filePath || typeof filePath !== 'string') {
        throw new Error('Invalid file path provided');
      }

      // Check if file exists in storage before generating signed URL
      const { data: fileExists, error: listError } = await supabase
        ?.storage
        ?.from(AVATAR_CONFIG?.STORAGE_BUCKET)
        ?.list(filePath?.split('/')?.slice(0, -1)?.join('/'), {
          limit: 1,
          search: filePath?.split('/')?.pop()
        });

      if (listError) {
        console.error('Error checking file existence:', listError);
        throw new Error(`Failed to verify file existence: ${listError?.message}`);
      }

      if (!fileExists || fileExists?.length === 0) {
        console.error('File not found in storage:', filePath);
        throw new Error(`Avatar file not found in storage: ${filePath}`);
      }

      const { data, error } = await supabase
        ?.storage
        ?.from(AVATAR_CONFIG?.STORAGE_BUCKET)
        ?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Signed URL generation error:', error);
        throw new Error(`Supabase signed URL error: ${error?.message}`);
      }

      if (!data?.signedUrl) {
        throw new Error('No signed URL returned from Supabase');
      }

      return data?.signedUrl;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      throw new Error(`Failed to generate avatar URL: ${error?.message || 'Unknown error'}`);
    }
  },

  /**
   * Creates cache entry in database
   * @param {string} seed - Avatar seed
   * @param {Object} config - Avatar configuration
   * @param {string} svgContent - SVG content
   * @param {string} storagePath - Supabase storage path
   * @returns {Promise<Object>} Created cache entry
   */
  async createCacheEntry(seed, config, svgContent, storagePath) {
    try {
      const configHash = this.generateConfigHash(config);
      const fileSize = new Blob([svgContent])?.size;

      const { data, error } = await supabase
        ?.from('avatar_cache')
        ?.insert({
          seed,
          config_hash: configHash,
          svg_content: svgContent,
          storage_path: storagePath, // Store path instead of URL
          file_size: fileSize
        })
        ?.select()
        ?.single();

      if (error) {
        // If duplicate key error, update existing entry instead
        if (error?.code === '23505') {
          console.log('Cache entry exists, updating with new data');
          const { data: updateData, error: updateError } = await supabase
            ?.from('avatar_cache')
            ?.update({
              config_hash: configHash,
              svg_content: svgContent,
              storage_path: storagePath,
              file_size: fileSize,
              accessed_at: new Date()?.toISOString(),
              access_count: 1 // Reset access count for regenerated avatar
            })
            ?.eq('seed', seed)
            ?.select()
            ?.single();

          if (updateError) {
            throw updateError;
          }

          return updateData;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error creating cache entry:', error);
      throw new Error('Failed to cache avatar');
    }
  },

  /**
   * Updates user's avatar configuration in database
   * @param {string} userId - User ID
   * @param {Object} config - Avatar configuration
   * @param {string} seed - Avatar seed
   * @param {string} storagePath - Supabase storage path
   * @param {string} svgContent - SVG content (optional)
   * @returns {Promise<Object>} Updated user profile
   */
  async updateUserAvatar(userId, config, seed, storagePath, svgContent) {
    try {
// Use UPDATE instead of UPSERT to avoid violating NOT NULL constraint on email
// User profile must exist (created by auth trigger) before updating avatar
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          avatar_seed: seed,
          avatar_config: config,
          avatar_style: AVATAR_CONFIG?.STYLE,
          avatar_storage_path: storagePath, // Store path for signed URL generation
          avatar_svg: svgContent, // Store SVG content in database
          avatar_updated_at: new Date()?.toISOString()
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error updating user avatar:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateUserAvatar:', error);
      throw new Error('Failed to update user avatar');
    }
  },

  /**
   * Gets user's current avatar configuration with signed URL
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User's avatar data with signed URL
   */
  async getUserAvatar(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('avatar_seed, avatar_config, avatar_style, avatar_storage_path, avatar_updated_at, avatar_svg')
        ?.eq('id', userId)
        ?.single();

      // Handle case where user profile doesn't exist yet (PGRST116 = 0 rows)
      if (error && error?.code === 'PGRST116') {
        return null;
      }

      if (error) {
        throw error;
      }

      // Generate signed URL if storage path exists
      if (data?.avatar_storage_path) {
        const signedUrl = await this.getSignedAvatarUrl(data?.avatar_storage_path);
        return {
          ...data,
          avatar_url: signedUrl
        };
      }

      return data;
    } catch (error) {
      console.error('Error fetching user avatar:', error);
      return null;
    }
  },

  /**
   * Main method to get or create avatar with hybrid caching
   * @param {string} userId - User ID
   * @param {Object} config - Avatar configuration
   * @param {string} gender - 'male' or 'female' for seed selection
   * @returns {Promise<Object>} Avatar data with signed URL
   */
  async getOrCreateAvatar(userId, config, gender = 'male') {
    try {
      const validatedConfig = this.validateConfig(config);
      const seed = AVATAR_CONFIG?.SEEDS?.[gender] || AVATAR_CONFIG?.SEEDS?.male;
      const configHash = this.generateConfigHash(validatedConfig);
      
      const cachedAvatar = await this.checkCache(seed, configHash);
      
      if (cachedAvatar && cachedAvatar?.storage_path) {
        try {
          await this.updateCacheAccess(cachedAvatar?.id);
          
          // Verify file exists before generating signed URL
          const signedUrl = await this.getSignedAvatarUrl(cachedAvatar?.storage_path);
          
          // If file doesn't exist (null returned), regenerate avatar
          if (!signedUrl) {
            console.warn('Cached file missing, regenerating avatar for seed:', seed);
            // Continue to regeneration below
          } else {
            // Store SVG content in user profile when using cached avatar
            await this.updateUserAvatar(userId, validatedConfig, seed, cachedAvatar?.storage_path, cachedAvatar?.svg_content);
            
            return {
              avatarUrl: signedUrl,
              storagePath: cachedAvatar?.storage_path,
              seed: seed,
              config: validatedConfig,
              cached: true
            };
          }
        } catch (signedUrlError) {
          console.warn('Error accessing cached avatar, regenerating:', signedUrlError);
          // If cached file doesn't exist, continue to regenerate
        }
      }
      
      // Generate new avatar
      const dicebearUrl = this.buildDiceBearURL(validatedConfig, seed, gender);
      const svgContent = await this.fetchAvatarSVG(dicebearUrl);
      const sanitizedSVG = this.sanitizeSVG(svgContent);
      
      const filename = this.generateFilename(seed, configHash);
      const storagePath = await this.uploadSVGToStorage(sanitizedSVG, filename, userId);
      
      // Verify upload success before generating signed URL
      if (!storagePath) {
        throw new Error('Failed to get storage path after upload');
      }

      // Small delay to ensure file is available in storage
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Generate signed URL for new avatar
      const signedUrl = await this.getSignedAvatarUrl(storagePath);
      
      await this.createCacheEntry(seed, validatedConfig, sanitizedSVG, storagePath);
      // Store SVG content in user profile when creating new avatar
      await this.updateUserAvatar(userId, validatedConfig, seed, storagePath, sanitizedSVG);
      
      return {
        avatarUrl: signedUrl,
        storagePath,
        seed: seed,
        config: validatedConfig,
        cached: false
      };
    } catch (error) {
      console.error('Error in getOrCreateAvatar:', error);
      
      // Return fallback DiceBear URL if all else fails
      const fallbackSeed = AVATAR_CONFIG?.SEEDS?.[gender] || AVATAR_CONFIG?.SEEDS?.male;
      const fallbackUrl = this.buildDiceBearURL(config || {}, fallbackSeed, gender);
      
      return {
        avatarUrl: fallbackUrl,
        storagePath: null,
        seed: fallbackSeed,
        config: config || {},
        cached: false,
        error: error?.message
      };
    }
  },

  /**
   * Generates default avatar for new users
   * @param {string} userId - User ID
   * @param {string} gender - 'male' or 'female'
   * @returns {Promise<Object>} Generated avatar data
   */
  async generateDefaultAvatar(userId, gender = 'male') {
    return await this.getOrCreateAvatar(userId, DEFAULT_AVATAR_CONFIG, gender);
  },

  /**
   * Deletes cached avatar from storage
   * @param {string} storagePath - Supabase storage path
   */
  async deleteCachedAvatar(storagePath) {
    try {
      const { error } = await supabase
        ?.storage
        ?.from(AVATAR_CONFIG?.STORAGE_BUCKET)
        ?.remove([storagePath]);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting cached avatar:', error);
    }
  },

  /**
   * Cleans up old cache entries
   * @param {number} daysOld - Age threshold in days
   */
  async cleanupOldCache(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - daysOld);
      
      const { data, error } = await supabase
        ?.from('avatar_cache')
        ?.select('*')
        ?.lt('accessed_at', cutoffDate?.toISOString())
        ?.lt('access_count', 5);

      if (error) {
        throw error;
      }

      for (const entry of data || []) {
        await this.deleteCachedAvatar(entry?.storage_path);
        await supabase?.from('avatar_cache')?.delete()?.eq('id', entry?.id);
      }

      return { cleaned: data?.length || 0 };
    } catch (error) {
      console.error('Error cleaning up cache:', error);
      return { error };
    }
  }
};